/**
 * Build the download filename for a generated day-report PDF.
 *
 * ADR-0018 §5 prescribes:
 *
 *     Dagrapport_<project>_<DD-MM-YYYY>_rev<revision>.pdf
 *
 * The project segment uses the human-readable project code (for example `AMS11`), never the
 * internal project UUID — ADR-0018 §4 forbids exposing internal identifiers in the document.
 *
 * The code is upper-cased for display only. The stored `project_code` is lower-case and is used
 * verbatim to build the Dropbox archive path in `dbvdw-archive-worker`; changing the stored value
 * would fork the archive root, so normalisation happens here at the presentation layer.
 *
 * Dates are rendered day-first to match both Dutch convention and the existing Dropbox folder
 * naming (`14-07-2026 - Dinsdag`). Note that day-first names do not sort chronologically in a
 * file listing.
 *
 * Segments are omitted rather than filled with placeholder text when the caller cannot supply
 * them, so a missing project code degrades to the previous filename shape instead of producing
 * `Dagrapport_undefined_…`.
 *
 * @param {Object} [input]
 * @param {string|null} [input.projectCode] Human-readable project code, e.g. `ams11`.
 * @param {string|null} [input.reportDate] ISO date of the report, e.g. `2026-07-14`.
 * @param {number|string|null} [input.revision] Report revision number.
 * @returns {string} A filesystem-safe filename ending in `.pdf`.
 */
export function buildPdfFilename({ projectCode, reportDate, revision } = {}) {
  const parts = ["Dagrapport"];

  const code = sanitizeSegment(projectCode).toUpperCase();
  if (code) parts.push(code);

  const date = sanitizeSegment(formatDayFirst(reportDate));
  if (date) parts.push(date);

  const rev = sanitizeSegment(revision);
  if (rev) parts.push(`rev${rev}`);

  return `${parts.join("_")}.pdf`;
}

/**
 * Resolve a project code from the access context returned by `GET /context`.
 *
 * @param {Object|null|undefined} accessContext Access context with a `projects` array.
 * @param {string|null|undefined} projectId Internal project UUID to look up.
 * @returns {string|null} The project code, or null when it cannot be resolved.
 */
export function projectCodeFromContext(accessContext, projectId) {
  if (!accessContext || !projectId) return null;
  const projects = accessContext.projects || [];
  const match = projects.find((item) => item && item.project_id === projectId);
  return match?.project_code || null;
}

/**
 * Render an ISO `YYYY-MM-DD` date as `DD-MM-YYYY`.
 *
 * Anything that is not a plain ISO date is returned unchanged, so an unexpected shape degrades to
 * the original value rather than to a wrong date.
 *
 * @param {string|null|undefined} value
 * @returns {string} The day-first date, or the input unchanged.
 */
export function formatDayFirst(value) {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return text;
  const [, year, month, day] = match;
  return `${day}-${month}-${year}`;
}

/**
 * Strip characters that are unsafe or confusing in a filename, and collapse whitespace.
 * Returns an empty string for values that carry no information.
 */
function sanitizeSegment(value) {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text) return "";
  return text
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "-")
    .replace(/_+/g, "-");
}
